import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { getToken, formatApiErrorDetail } from "@/lib/api";
import {
  FileSignature, Send, Loader2, Bell, Ban, Download, StickyNote, ShieldQuestion,
  CheckCircle2, Clock, Eye, XCircle, History, Sparkles, Gauge, MousePointerClick, Timer, ScrollText,
  TrendingUp, Phone, MessageSquare, Mail, PauseCircle,
} from "lucide-react";

const CONV_COLORS = {
  ready_to_sign: "text-emerald-400", high_probability: "text-cyan-400", signed: "text-emerald-400",
  needs_follow_up: "text-amber-400", losing_interest: "text-orange-400", cold_lead: "text-red-400",
};
const METHOD_ICON = { call: Phone, sms: MessageSquare, email: Mail, wait: PauseCircle, none: CheckCircle2 };

const STATUS = {
  sent: { label: "Sent", cls: "bg-cyan-500/15 text-cyan-300", Icon: Send },
  viewed: { label: "Viewed", cls: "bg-amber-500/15 text-amber-300", Icon: Eye },
  signed: { label: "Signed", cls: "bg-emerald-500/15 text-emerald-300", Icon: CheckCircle2 },
  declined: { label: "Declined", cls: "bg-red-500/15 text-red-300", Icon: XCircle },
  revoked: { label: "Revoked", cls: "bg-slate-600/30 text-slate-300", Icon: Ban },
};

// Attorney-facing retainer control panel (rendered on Case Intelligence).
export default function RetainerPanel() {
  const { caseId } = useParams();
  const [state, setState] = useState(null);
  const [tpls, setTpls] = useState([]);
  const [tplId, setTplId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/cases/${caseId}/retainer`);
      setState(data);
      if (data.can_manage) api.get("/retainers/templates").then((r) => setTpls(r.data)).catch(() => {});
    } catch (e) { if (e?.response?.status === 403) setState({ forbidden: true }); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [caseId]);

  if (!state) return <div className="mt-8 flex justify-center py-8"><Loader2 className="w-5 h-5 text-cyan-400 animate-spin" /></div>;
  if (state.forbidden || !state.can_manage) return null;

  const r = state.retainer;
  const token = getToken();
  const active = r && ["sent", "viewed"].includes(r.status);
  const conflictNeeded = state.conflict_required && !state?.case_conflict_check?.completed;

  const act = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); await load(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); } };
  const send = () => act(() => api.post(`/cases/${caseId}/retainer/send`, tplId ? { template_id: tplId } : {}), "Retainer sent to client");
  const remind = () => act(() => api.post(`/cases/${caseId}/retainer/remind`, {}), "Reminder sent");
  const revoke = () => act(() => api.post(`/cases/${caseId}/retainer/revoke`, {}), "Retainer revoked");
  const conflict = () => act(() => api.post(`/cases/${caseId}/retainer/conflict-check`, { completed: true }), "Conflict check recorded");
  const addNote = () => { if (!note.trim()) return; act(() => api.post(`/cases/${caseId}/retainer/notes`, { note }), "Note added").then(() => setNote("")); };
  const askAi = async () => { setAiBusy(true); try { const { data } = await api.post(`/cases/${caseId}/retainer/ai-analyze`, {}); setAi(data); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setAiBusy(false); } };

  const S = r ? STATUS[r.status] : null;
  const intel = state.intelligence;
  const fmtTime = (s) => { s = s || 0; const m = Math.floor(s / 60); return m ? `${m}m ${s % 60}s` : `${s}s`; };
  const scoreColor = (v) => (v >= 75 ? "text-emerald-400" : v >= 55 ? "text-cyan-400" : v >= 30 ? "text-amber-400" : "text-red-400");

  return (
    <div className="mt-8 rounded-3xl border border-slate-800 bg-navy text-slate-100 p-6" data-testid="retainer-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-medium text-cyan-400 uppercase tracking-widest flex items-center gap-1.5"><FileSignature className="w-3.5 h-3.5" /> Automatic Retainer Delivery</p>
          <h2 className="font-heading text-2xl font-extrabold text-white mt-1">Retainer & E-Signature</h2>
        </div>
        {S && <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${S.cls}`} data-testid="retainer-status"><S.Icon className="w-3.5 h-3.5" /> {S.label}</span>}
      </div>

      {conflictNeeded && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 flex-wrap">
          <span className="flex items-center gap-2 text-amber-300 text-sm"><ShieldQuestion className="w-4 h-4" /> A conflict check is required before sending.</span>
          <button onClick={conflict} disabled={busy} data-testid="retainer-conflict-check" className="px-3 py-1.5 rounded-full bg-amber-400 text-slate-900 text-xs font-semibold hover:bg-amber-300">Mark conflict check done</button>
        </div>
      )}
      {state?.case_conflict_check?.completed && <p className="mt-3 text-xs text-emerald-400 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Conflict check by {state.case_conflict_check.completed_by}</p>}

      {/* Retainer Intelligence™ */}
      {r && intel && (
        <div className="mt-5 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-900/40 border border-slate-800 p-5" data-testid="retainer-intel">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs font-medium text-cyan-400 uppercase tracking-widest flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" /> Retainer Intelligence™</p>
            <div className="text-right">
              <div className={`text-4xl font-extrabold tabular-nums leading-none ${scoreColor(intel.engagement_score)}`} data-testid="retainer-engagement-score">{intel.engagement_score}<span className="text-lg text-slate-500">%</span></div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wide mt-0.5">{intel.band_label}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            {[["Opens", intel.opens, MousePointerClick], ["Reading time", fmtTime(intel.reading_seconds), Timer], ["Scroll", `${intel.max_scroll_pct}%`, ScrollText], ["Returns", intel.return_visits, History]].map(([l, v, Ic]) => (
              <div key={l} className="rounded-xl bg-slate-900/60 border border-slate-800 p-3">
                <div className="text-[10px] text-slate-400 uppercase tracking-wide flex items-center gap-1"><Ic className="w-3 h-3" /> {l}</div>
                <div className="mt-1 text-lg font-extrabold text-white tabular-nums">{v}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-2 text-[11px] text-slate-400">
            <div>First opened: <span className="text-slate-200">{intel.first_opened_at ? new Date(intel.first_opened_at).toLocaleString() : "—"}</span></div>
            <div>Last viewed: <span className="text-slate-200">{intel.last_viewed_at ? new Date(intel.last_viewed_at).toLocaleString() : "—"}</span></div>
            <div>Signature page: <span className="text-slate-200">{intel.reached_signature_page ? "Reached ✓" : "Not reached"}</span></div>
            <div>Device: <span className="text-slate-200">{[intel.device, intel.browser, intel.os].filter(Boolean).join(" · ") || "—"}{intel.city ? ` · ${intel.city}` : ""}</span></div>
          </div>
          {/* AI recommendation */}
          <div className="mt-4 rounded-xl bg-cyan-500/10 border border-cyan-500/25 p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-semibold text-cyan-300 flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> {intel.recommendation?.label}</div>
              <button onClick={askAi} disabled={aiBusy} data-testid="retainer-ask-ai" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500 text-slate-950 text-xs font-semibold hover:bg-cyan-400 disabled:opacity-50">{aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Ask AI</button>
            </div>
            <p className="mt-1.5 text-xs text-slate-300" data-testid="retainer-recommendation-why">{intel.recommendation?.why}</p>
            {ai && (
              <div className="mt-3 pt-3 border-t border-cyan-500/20 text-xs text-slate-200" data-testid="retainer-ai-result">
                <div className="font-semibold text-cyan-300">AI deep-dive · {ai.confidence}% confidence</div>
                <p className="mt-1 text-slate-300">{ai.suggested_action || ai.analysis}</p>
                {ai.best_time_to_contact && <p className="mt-1 text-slate-400">Best time to contact: {ai.best_time_to_contact}</p>}
              </div>
            )}
          </div>

          {/* Retainer Conversion Score™ — predictive */}
          {intel.conversion && (() => {
            const c = intel.conversion; const MI = METHOD_ICON[c.recommended_follow_up_method] || CheckCircle2;
            return (
              <div className="mt-3 rounded-xl bg-slate-900/70 border border-slate-800 p-4" data-testid="retainer-conversion">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs font-medium text-slate-300 uppercase tracking-widest flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-cyan-400" /> Retainer Conversion Score™</p>
                  <div className="text-right">
                    <span className={`text-3xl font-extrabold tabular-nums ${CONV_COLORS[c.band] || "text-slate-200"}`} data-testid="retainer-conversion-score">{c.score}<span className="text-sm text-slate-500">%</span></span>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wide">{c.band_label}</div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {[["Prob. of signing", `${c.probability_pct}%`], ["Est. time to sign", c.predicted_time_to_sign], ["Abandon risk", `${c.abandonment_likelihood_pct}%`]].map(([l, v]) => (
                    <div key={l} className="rounded-lg bg-slate-900/60 border border-slate-800 p-2.5">
                      <div className="text-[9px] text-slate-500 uppercase tracking-wide">{l}</div>
                      <div className="mt-0.5 text-sm font-bold text-white">{v}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 px-3 py-2">
                  <MI className="w-4 h-4 text-cyan-300 shrink-0" />
                  <div className="text-xs text-slate-200">
                    <span className="font-semibold text-cyan-300">{c.recommendation?.label}</span>
                    <span className="text-slate-400"> · {c.recommended_follow_up_time}</span>
                    <div className="text-[11px] text-slate-400 mt-0.5">{c.recommendation?.why}</div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* No active retainer → send */}
      {!active && r?.status !== "signed" && (
        <div className="mt-5 flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="text-[11px] text-slate-400">Template</span>
            <select value={tplId} onChange={(e) => setTplId(e.target.value)} data-testid="retainer-template-select"
              className="mt-1 block w-64 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-cyan-500">
              <option value="">Default ({(state.retainer?.language) || "firm"} template)</option>
              {tpls.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}{t.is_default ? " ★" : ""} · {t.case_type}</option>)}
            </select>
          </label>
          <button onClick={send} disabled={busy || conflictNeeded} data-testid="retainer-send" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-500 text-slate-950 text-sm font-semibold hover:bg-cyan-400 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {r?.status === "declined" ? "Re-send new retainer" : "Send retainer"}
          </button>
        </div>
      )}

      {/* Active retainer → manage */}
      {active && (
        <div className="mt-5">
          <div className="text-sm text-slate-300">{r.template_name} · sent {new Date(r.sent_at).toLocaleString()} · {r.reminders_sent} reminder(s)</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/retainer/preview?auth=${token}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-800 border border-slate-700 text-sm text-slate-200 hover:bg-slate-700"><Eye className="w-4 h-4" /> Preview</a>
            <button onClick={remind} disabled={busy} data-testid="retainer-remind" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-800 border border-slate-700 text-sm text-slate-200 hover:bg-slate-700"><Bell className="w-4 h-4" /> Send reminder</button>
            <button onClick={revoke} disabled={busy} data-testid="retainer-revoke" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/30 text-sm text-red-300 hover:bg-red-500/20"><Ban className="w-4 h-4" /> Revoke</button>
          </div>
        </div>
      )}

      {/* Signed */}
      {r?.status === "signed" && (
        <div className="mt-5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-4">
          <div className="text-sm text-emerald-300 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Signed {new Date(r.signed_at).toLocaleString()} by {r.signature?.name} ({r.signature?.type})</div>
          <div className="mt-1 text-[11px] text-slate-400 break-all">Hash: {r.content_hash}</div>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/retainer/download?auth=${token}`} data-testid="retainer-download" className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-slate-950 text-sm font-semibold hover:bg-emerald-400"><Download className="w-4 h-4" /> Download signed PDF</a>
        </div>
      )}

      {/* Internal notes */}
      {r && (
        <div className="mt-6">
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5" /> Internal notes (attorney only)</h4>
          <div className="mt-2 space-y-1.5">
            {(r.internal_notes || []).map((n, i) => (
              <div key={i} className="text-sm text-slate-300 bg-slate-900/60 rounded-lg px-3 py-2" data-testid={`retainer-note-${i}`}>{n.note}<span className="block text-[10px] text-slate-500 mt-0.5">{n.author_name} · {new Date(n.created_at).toLocaleString()}</span></div>
            ))}
            {!(r.internal_notes || []).length && <p className="text-xs text-slate-500">No notes yet.</p>}
          </div>
          <div className="mt-2 flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="retainer-note-input" placeholder="Add an internal note…" className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-cyan-500" />
            <button onClick={addNote} data-testid="retainer-note-add" className="px-4 py-2 rounded-lg bg-slate-700 text-sm text-white hover:bg-slate-600">Add</button>
          </div>
        </div>
      )}

      {/* Audit trail */}
      {r?.events?.length > 0 && (
        <div className="mt-6">
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Audit trail</h4>
          <div className="mt-2 space-y-1">
            {r.events.map((e, i) => (
              <div key={i} className="text-xs text-slate-400 flex items-center gap-2" data-testid={`retainer-event-${i}`}>
                <Clock className="w-3 h-3 text-slate-600" />
                <span className="text-slate-200 capitalize">{e.action}</span>
                {e.actor_name && <span>· {e.actor_name}</span>}
                <span className="text-slate-600">· {new Date(e.at).toLocaleString()}</span>
                {e.ip && <span className="text-slate-600">· {e.ip}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
